import throttle from 'lodash/throttle';
import Vue from 'vue';
import { EventType, RouteName } from '@/shared/enums';
import subscriberError from '@/shared/utils/subscriber-error';
import { metaSetup } from '@/shared/utils/seo';
import { setDefaultAudioLang } from '@/shared/components/player/store';

export default async ({ $EventBus, $i18n, $oneTrust, $router, $services, $spinner, $stats, $storage }) => {
  const info = $services.resolve('info');
  const subscriber = $services.resolve('subscriber');
  const translations = $services.resolve('translations');
  const menu = $services.resolve('menu');
  const search = $services.resolve('search');
  const footer = $services.resolve('footer');
  const agreementTenant = $services.resolve('agreementTenant');
  const categories = $services.resolve('categories');
  const liveManager = $services.resolve('liveManager');
  const time = $services.resolve('time');
  const favourites = $services.resolve('favourites');
  const badges = $services.resolve('badges');
  const bookmarks = $services.resolve('bookmarks');

  let fetchBookmarksPromise;
  let fetchResourcesPromise;
  let fetchUserInfoPromise;

  const refreshBookmarks = (opts = {}) => {
    if (fetchBookmarksPromise) {
      return fetchBookmarksPromise;
    }

    fetchBookmarksPromise = Promise.all([
      bookmarks.refresh(opts),
      favourites.refresh(opts),
    ]).then(() => {
      if (opts.routerRefresh) {
        const isKidsMode = $storage.isKidsMode;
        const isKidsLabel = ($router.currentRoute.meta?.label || $router.currentRoute.params?.label || '').startsWith('kids');

        if (isKidsMode && !isKidsLabel) {
          return $router.push({ name: RouteName.KIDS_HOME });
        }

        if (!isKidsMode && isKidsLabel) {
          return $router.push({ name: RouteName.HOME });
        }

        $router.reload();
      }
    }).finally(() => {
      fetchBookmarksPromise = null;
    });

    return fetchBookmarksPromise;
  };

  const fetchResources = () => {
    if (fetchResourcesPromise) {
      return fetchResourcesPromise;
    }

    fetchResourcesPromise = Promise.all([
      translations.fetch(),
      menu.fetch(),
      search.fetch(),
      footer.fetch(),
      badges.fetchAll('', { reset: true }),
      agreementTenant.fetch(),
      categories.fetchAll('', { reset: true }),
    ]).then(() => {
      $i18n.setLocaleMessage(subscriber.currentLanguage, translations.data);
      $i18n.locale = subscriber.currentLanguage;
    }).finally(() => {
      fetchResourcesPromise = null;
    });

    return fetchResourcesPromise;
  };

  $EventBus.on(EventType.TECHNICAL_ERROR, Vue.config.errorHandler);

  window.addEventListener('orientationchange', () => {
    window.addEventListener('resize', () => $EventBus.emit(EventType.RESIZE), { once: true });
  });
  window.addEventListener('focus', () => subscriber.refreshHttpSession());
  window.addEventListener('resize', throttle(() => $EventBus.emit(EventType.RESIZE), 300, { leading: false }));
  window.addEventListener('scroll', throttle(() => $EventBus.emit(EventType.SCROLL), 300), { passive: true });

  info.on('sync', () => setDefaultAudioLang(info.defaultLanguage)); // @todo - handle all cases inside player
  subscriber.on('error', (code) => subscriberError(code, subscriber));
  subscriber.on('login', () => {
    refreshBookmarks({ cookieRefresh: true });
    $stats.login({ isAutoLogin: false });
  });
  subscriber.on('login:auto', () => {
    refreshBookmarks();
    $stats.login({ isAutoLogin: true });
  });
  subscriber.on('logout:stats', (params) => $stats.logout(params));
  subscriber.on('logout', () => {
    const route = $router.currentRoute;

    refreshBookmarks();

    if (route.meta.requireAuth) {
      $router.replace({ name: RouteName.SUBSCRIBER_LOGIN, query: { redirect: route.fullPath } });
    }
  });
  subscriber.on('sync:language', (lang) => {
    $spinner.show();
    $stats.changeLanguage(lang);
    liveManager.clearPrograms({ force: true });
    $oneTrust.changeLanguage(subscriber.currentLanguage);

    refreshBookmarks()
      .then(() => fetchResources())
      .then(() => $router.reload())
      .catch((error) => $EventBus.emit(EventType.TECHNICAL_ERROR, error));
  });
  subscriber.on('sync:kids', () => refreshBookmarks({ routerRefresh: true }));
  subscriber.on('sync:profile', () => refreshBookmarks({
    cookieRefresh: true,
    routerRefresh: true,
  }));
  subscriber.on('sync:tenant', () => {
    info.fetch({ sync: true })
      .then(() => fetchResources())
      .then(() => liveManager.channels.fetchAll('', { reset: true }))
      .catch((error) => $EventBus.emit(EventType.TECHNICAL_ERROR, error));
  });
  subscriber.on('change:profile', ({ previousProfile, profile }) => {
    $stats.changeProfile(previousProfile, profile);
  });

  $services.resolve('profiles');
  $services.resolve('avatars');

  const fetchUserInfo = () => {
    if (fetchUserInfoPromise) {
      return fetchUserInfoPromise;
    }

    fetchUserInfoPromise = subscriber.updateAuthToken()
      .finally(() => {
        fetchUserInfoPromise = null;
      });

    return fetchUserInfoPromise;
  };

  await info.fetch()
    .then(() => subscriber.fetch())
    .then(() => fetchUserInfo())
    .then(() => liveManager.init(time))
    .then(() => fetchResources())
    .then(() => {
      setDefaultAudioLang(info.defaultLanguage);
      metaSetup({ fbAppId: info.facebookAppId });

      if (info.appleConfiguration?.appId) {
        window.localStorage.setItem('appleAppId', info.appleConfiguration.appId);
      }

      if (info.platformSettings?.errorPageReloadTimeout) {
        window.localStorage.setItem('errorPageReloadTimeout', info.platformSettings.errorPageReloadTimeout * 1000);
      }
    })
    .catch((error) => $EventBus.emit(EventType.TECHNICAL_ERROR, error));
};
